import React from 'react';
import { TagTypes } from '../../../Api/Types/tagTypes';
import { bankDocumentsApi } from '../Api/bankDocumentsApi';
import { normalizePositiveInteger } from './lookupPagination';
import {
  mapExpenseInvoiceToBankDocumentLookup,
  type BankDocumentExpenseInvoiceLookup,
  type BankDocumentExpenseInvoiceLookupSource,
} from './expenseInvoicesLookupMapper';

interface BankDocumentExpenseInvoiceLookupArgs {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  companyId?: number | null;
}

interface BankDocumentExpenseInvoicesLookupRequest {
  searchTerm?: string;
  pageNumber?: number;
  pageSize?: number;
  companyId?: number | null;
}

interface BankDocumentExpenseInvoicesPagedResponse {
  items?: BankDocumentExpenseInvoiceLookupSource[] | null;
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
}

export const buildBankDocumentExpenseInvoicesLookupUrl = (
  request: BankDocumentExpenseInvoicesLookupRequest
): string => {
  const queryParams = new URLSearchParams();
  const normalizedSearchTerm = request.searchTerm?.trim();
  const normalizedPageNumber = normalizePositiveInteger(request.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(request.pageSize, 10);

  if (normalizedSearchTerm) {
    queryParams.set('search', normalizedSearchTerm);
  }

  if (request.companyId !== null && request.companyId !== undefined) {
    queryParams.set('companyId', request.companyId.toString());
  }

  queryParams.set(
    'pageNumber',
    normalizedPageNumber.toString()
  );
  queryParams.set('pageSize', normalizedPageSize.toString());

  return `/bank-documents/lookup/expense-invoices?${queryParams.toString()}`;
};

const bankDocumentExpenseInvoicesLookupApi = bankDocumentsApi.injectEndpoints({
  endpoints: (builder) => ({
    getBankDocumentExpenseInvoicesLookupRaw: builder.query<
      BankDocumentExpenseInvoicesPagedResponse,
      BankDocumentExpenseInvoicesLookupRequest
    >({
      query: buildBankDocumentExpenseInvoicesLookupUrl,
      providesTags: [{ type: TagTypes.BankDocument, id: TagTypes.List }],
    }),
  }),
});

const { useGetBankDocumentExpenseInvoicesLookupRawQuery } = bankDocumentExpenseInvoicesLookupApi;

type ExpenseInvoicesLookupQueryOptions = Parameters<
  typeof useGetBankDocumentExpenseInvoicesLookupRawQuery
>[1];
type ExpenseInvoicesLookupQueryResult = ReturnType<
  typeof useGetBankDocumentExpenseInvoicesLookupRawQuery
>;
type BankDocumentExpenseInvoicesLookupQueryResult = Pick<
  ExpenseInvoicesLookupQueryResult,
  'isLoading' | 'isFetching' | 'isError' | 'error' | 'refetch'
> & {
  data: BankDocumentExpenseInvoiceLookup[];
  pagination: {
    totalCount: number;
    pageNumber: number;
    pageSize: number;
  };
};

export const useGetBankDocumentExpenseInvoicesLookupQuery = (
  args: BankDocumentExpenseInvoiceLookupArgs,
  options?: ExpenseInvoicesLookupQueryOptions
): BankDocumentExpenseInvoicesLookupQueryResult => {
  const queryOptions: ExpenseInvoicesLookupQueryOptions = options;
  const normalizedSearchTerm = args.searchTerm?.trim() || undefined;
  const normalizedPageNumber = normalizePositiveInteger(args.pageNumber, 1);
  const normalizedPageSize = normalizePositiveInteger(args.pageSize, 10);

  const queryResult = useGetBankDocumentExpenseInvoicesLookupRawQuery(
    {
      searchTerm: normalizedSearchTerm,
      pageNumber: normalizedPageNumber,
      pageSize: normalizedPageSize,
      companyId: args.companyId ?? null,
    },
    queryOptions
  );

  const pagedResponse = queryResult.data;

  const data = React.useMemo(
    () =>
      (pagedResponse?.items ?? [])
        .map((expenseInvoice: BankDocumentExpenseInvoiceLookupSource) =>
          mapExpenseInvoiceToBankDocumentLookup(expenseInvoice)
        )
        .filter(
          (
            item: BankDocumentExpenseInvoiceLookup | undefined
          ): item is BankDocumentExpenseInvoiceLookup => Boolean(item)
        ),
    [pagedResponse]
  );

  return {
    data,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
    pagination: {
      totalCount: pagedResponse?.totalCount ?? 0,
      pageNumber: pagedResponse?.pageNumber ?? normalizedPageNumber,
      pageSize: pagedResponse?.pageSize ?? normalizedPageSize,
    },
  };
};
